import isEmpty from 'lodash/isEmpty';
import ProductSearch from 'services/ProductSearch';

import {
  SELECT_ALT_PRODUCT,
  RECEIVE_SEARCH_ALTERNATIVES,
  RECEIVE_TOTAL_ALTERNATIVES,
  UPDATE_PRODUCT_QUANTITY,
  SELECT_PRODUCT_FILTER_OPTIONS,
  UPDATE_FILTERS_APPLIED_COUNT,
  SELECT_PRODUCT,

  CLEAR_PRODUCT_FILTER,
  CLEAR_PRODUCT_FILTERS,
  FILTER_SELECTED_PRODUCT,
  RECEIVE_COLORS,
  RECEIVE_MATERIALS,
  RESET,
  SEARCH_KEY,
  SET_VARIANTS_BY_ATTRIBUTE,
  SET_VARIANT_PRODUCT_IDS,
  SET_VARIANT_LOAD_STATUS,
  CLEAR_VARIANT_PRODUCT_IDS
} from './action-constants';

import {
  getAlternativeProduct,
  getFilterValues,
  getSelectedProduct
} from './selectors';

import { Product } from 'redux/models';

import {
  addStudioProducts,
  getIsSharedSpace,
  getProductFor,
  getProductVariantIds
} from 'redux/Studio';

import { variantProducts } from 'services/warehouse/api';

export const selectAltProduct = (product, partnerSlug) => (dispatch) => {
  dispatch({
    type: SELECT_ALT_PRODUCT,
    payload: product
  });

  dispatch(fetchVariantProducts(product));
  dispatch(searchAlternatives(product, partnerSlug));
};

const selectProduct = product => ({ type: SELECT_PRODUCT, payload: product });

const receiveSearchAlternatives = payload => ({
  type: RECEIVE_SEARCH_ALTERNATIVES,
  payload
});
const updateTotalAlternatives = payload => ({
  type: RECEIVE_TOTAL_ALTERNATIVES,
  payload
});

export const updateProductQuantity = payload => ({
  type: UPDATE_PRODUCT_QUANTITY,
  payload
});

export const selectProductAndGetAndClearAlternatives = (
  product,
  partnerSlug,
  callback
) => (dispatch, getState) => {
  const shareMode = getIsSharedSpace();
  if (shareMode) {
    dispatch(selectProduct(product));
    if (callback) dispatch(callback);
  } else {
    // dispatch select product before searching alternatives
    // to not block the rendering of PDP as search is fetching products
    dispatch(selectProduct(product));
    if (callback) dispatch(callback);

    const productConsidered = getAlternativeProduct(getState()) || product;

    return dispatch(searchAlternatives(productConsidered, partnerSlug));
  }
};

export const searchAlternatives = (product, partnerSlug) => (dispatch, getState) => {
  if (!product) { return; }

  if (!product.mainType()) {
    dispatch(receiveSearchAlternatives({ products: [] }));
    dispatch(updateTotalAlternatives({ total: 0 }));
    dispatch(filterSelectedProduct());
    return Promise.resolve();
  }

  const params = { page_size: 100, page: 1, ...additionalSearchAlternativesParams(product) };

  const filters = [];

  if (partnerSlug) {
    params.partner = partnerSlug;
  }

  if (product.styleTag()) {
    product.styleTag().forEach(tag => filters.push(`f=Style:${tag}`));
  }

  const filterValues = getFilterValues(getState());

  Object.keys(filterValues)
    .filter(filter => !isEmpty(filterValues[filter]))
    .forEach(filter => {
      filterValues[filter].forEach(filterData => filters.push(filterData));
    });

  const searchName = SEARCH_KEY + product.id();

  // Use new alternatives search
  if (!ProductSearch.searchExists({ name: searchName, state: getState() })) {
    dispatch(ProductSearch.createSearch({
      uniqueName: searchName,
      type: ProductSearch.SEARCH_TYPE_PRODUCT_ALTERNATIVES,
      productId: product.id(),
      partnerSlug
    }));
  }

  dispatch(ProductSearch.updateFilters({ name: searchName, enabledFilters: filterValues }));

  dispatch(ProductSearch.runSearch({
    name: searchName
  }));
};

export const additionalSearchAlternativesParams = (product) => {
  return { id: product.id(), mode: 'alternatives' };
};

export const receiveMaterials = payload => ({
  type: RECEIVE_MATERIALS,
  payload
});

export const receiveColors = payload => ({
  type: RECEIVE_COLORS,
  payload
});

export const selectProductFilterOptions = (
  filter,
  options,
  partnerSlug,
  isMobileView
) => (dispatch) => {
  let selected = options;

  dispatch({
    type: SELECT_PRODUCT_FILTER_OPTIONS,
    filter,
    options: selected
  });

  // on small screen widths, explicit button for applying filters
  if (!isMobileView) {
    dispatch(applyFilters(partnerSlug));
  }
};

export const clearProductFilter = (
  filter,
  isMobileView,
  partnerSlug
) => dispatch => {
  dispatch({
    type: CLEAR_PRODUCT_FILTER,
    filter
  });

  // on small screen widths, explicit button for applying filters
  if (!isMobileView) {
    dispatch(applyFilters(partnerSlug));
  }
};

export const filterSelectedProduct = () => dispatch => {
  dispatch({
    type: FILTER_SELECTED_PRODUCT
  });
};

export const applyFilters = (partnerSlug) => (dispatch, getState) => {
  const product = getAlternativeProduct(getState()) || getSelectedProduct(getState());

  dispatch({
    type: UPDATE_FILTERS_APPLIED_COUNT
  });

  dispatch(searchAlternatives(product, partnerSlug));
};

export const clearProductFilters = (partnerSlug) => dispatch => {
  dispatch({
    type: CLEAR_PRODUCT_FILTERS
  });

  dispatch(applyFilters(partnerSlug));
};

export const reset = () => ({
  type: RESET
});

export const fetchVariantProducts = (selectedProduct, partnerSlug) => {
  return async (dispatch, getState) => {
    const state = getState();
    const productId = selectedProduct.id();
    const productVariantsLoaded = selectedProduct && selectedProduct.variantsLoaded();
    if (productVariantsLoaded) {
      // Product Variants are already in redux we don't need to load them again
      // Get the variants using the product variant hash
      const productIds = getProductVariantIds({
        variantHash: selectedProduct.variantHash(),
        state
      });
      if (productIds.length > 0) {
        const variantProducts = productIds.map(productId => getProductFor({ productId, state }));
        dispatch(setAvailableVariantAttributes({ selectedProduct, variantProducts }));
      }
      dispatch(setVariantProductIds(productIds));
    } else {
      dispatch(setVariantLoadStatus(true));
      try {
        const prodResult = await variantProducts(productId, partnerSlug);
        let variantProductIds = [];
        let resultProducts = [];
        if (prodResult.results && prodResult.results.length) {
          variantProductIds = prodResult.results.map(prod => prod.id).filter(id => id !== productId);
          resultProducts = prodResult.results.map(wholeProd => {
            const product = Product.createProductFromWarehouseProductResponse({ whProduct: wholeProd });
            product.setVariantsLoaded(true);
            return product;
          }).filter(product => product.id() !== productId);
          dispatch(addStudioProducts([...resultProducts, selectedProduct]));
          selectedProduct && selectedProduct.setVariantsLoaded(true);
        }
        if (resultProducts.length > 0) {
          dispatch(setAvailableVariantAttributes({ selectedProduct, variantProducts: resultProducts }));
        }
        dispatch(setVariantProductIds(variantProductIds));
      } catch (err) {
        dispatch(setVariantProductIds([]));
      }
      dispatch(setVariantLoadStatus(false));
    }
  };
};

const sortByVolume = (product1, product2) => {
  return product1.volume() - product2.volume();
};

const setAvailableVariantAttributes = ({ selectedProduct, variantProducts }) => {
  // Check if is supported with valid data
  if (!selectedProduct || !variantProducts ||
    !Array.isArray(variantProducts) || !selectedProduct.hasAttributeVariants()) {
    return {
      type: SET_VARIANTS_BY_ATTRIBUTE,
      payload: {}
    };
  }
  variantProducts.push(selectedProduct);
  variantProducts.sort(sortByVolume);
  // Break attributes out by their type
  const availableAttributes = selectedProduct.getVariantAttributeTypes();
  let attributeMap = {};
  availableAttributes.forEach(attribute => {
    // Create a map of the variant products in the form of
    // attributeType: { attributeValue: [productIds] }
    // ex. 'color': { 'white': [12345, 123456] }
    const attributesValues = variantProducts.reduce((map, product) => {
      const key = [product.variantAttributes()[attribute]];
      const value = product.id();
      if (!map[key]) {
        map[key] = [];
      }
      return {
        ...map,
        [key]: [...map[key], value]
      };
    }, {});
    attributeMap = { ...attributeMap, [attribute]: attributesValues };
  });
  // Update the state
  return {
    type: SET_VARIANTS_BY_ATTRIBUTE,
    payload: attributeMap
  };
};

export const setVariantLoadStatus = (bool) => ({
  type: SET_VARIANT_LOAD_STATUS,
  payload: bool ? 1 : 0
});

export const setVariantProductIds = (payload) => ({
  type: SET_VARIANT_PRODUCT_IDS,
  payload
});

export const clearVariantProducts = () => ({
  type: CLEAR_VARIANT_PRODUCT_IDS
});
